/**
 * Hephaistion – Dokumentenverarbeitung
 * KI-Agent für OCR-Konvertierung, Texterkennung und Leerseiten-Entfernung
 */

import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FilePlus, 
  FileText, 
  Image, 
  X, 
  Download, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Zap,
  Trash2,
  FileDown,
  Languages,
  FileX,
  Inbox
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');
const API = `${BACKEND_URL}/api`;

// Unterstützte Formate
const SUPPORTED_FORMATS = {
  images: ['.png', '.jpg', '.jpeg', '.webp', '.tiff', '.bmp', '.gif'],
  documents: ['.pdf', '.docx', '.doc', '.odt', '.rtf']
};

const ALL_FORMATS = [...SUPPORTED_FORMATS.images, ...SUPPORTED_FORMATS.documents];

// Deutsche Statusübersetzungen
const STATUS_LABELS = {
  pending: "Wartend",
  processing: "Verarbeitung",
  completed: "Fertig",
  error: "Fehler"
};

const getFileIcon = (type) => {
  if (SUPPORTED_FORMATS.images.includes(type)) {
    return <Image className="w-5 h-5" />;
  }
  return <FileText className="w-5 h-5" />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileCard = ({ file, onRemove, onDownload, onSendToPosteingang, sendingId }) => {
  const statusStyles = {
    pending: "pending",
    processing: "processing",
    completed: "completed",
    error: "error"
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className={`file-card ${statusStyles[file.status]}`}
      data-testid={`file-card-${file.id}`}
    >
      {/* Fortschrittsbalken für Verarbeitung */}
      {file.status === "processing" && (
        <div className="progress-bar w-full overflow-hidden">
          <div className="h-full bg-[#0055FF] animate-scan w-1/3" />
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 bg-slate-50 rounded flex items-center justify-center text-slate-400 flex-shrink-0">
            {getFileIcon(file.type)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-mono text-sm font-medium truncate" title={file.name}>
              {file.name}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {formatFileSize(file.size)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Status Badge */}
          <span className={`status-badge ${statusStyles[file.status]}`}>
            {file.status === "processing" && <Loader2 className="w-3 h-3 animate-spin" />}
            {file.status === "completed" && <CheckCircle2 className="w-3 h-3" />}
            {file.status === "error" && <AlertCircle className="w-3 h-3" />}
            {STATUS_LABELS[file.status]}
          </span>
          
          {/* Aktionen */}
          {file.status === "completed" && (
            <>
              {!file.sent_to_posteingang ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2 text-violet-600 hover:bg-violet-50"
                  onClick={() => onSendToPosteingang(file)}
                  disabled={sendingId === file.id}
                  data-testid={`send-posteingang-btn-${file.id}`}
                  title="An Posteingang senden & analysieren"
                >
                  {sendingId === file.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />}
                </Button>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                  <CheckCircle2 className="w-3 h-3" /> Im Posteingang
                </span>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-emerald-600 hover:bg-emerald-50"
                onClick={() => onDownload(file)}
                data-testid={`download-btn-${file.id}`}
              >
                <Download className="w-4 h-4" />
              </Button>
            </>
          )}
          
          {file.status !== "processing" && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-slate-400 hover:text-red-500 hover:bg-red-50"
              onClick={() => onRemove(file.id)}
              data-testid={`remove-btn-${file.id}`}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Zusätzliche Info bei Abschluss */}
      {file.status === "completed" && (file.blank_pages_removed > 0 || file.ocr_skipped) && (
        <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap gap-2">
          {file.blank_pages_removed > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded">
              <FileX className="w-3 h-3" />
              {file.blank_pages_removed} leere Seiten entfernt
            </span>
          )}
          {file.ocr_skipped && (
            <span className="inline-flex items-center gap-1 text-xs text-blue-500 bg-blue-50 px-2 py-1 rounded">
              <CheckCircle2 className="w-3 h-3" />
              OCR bereits vorhanden
            </span>
          )}
        </div>
      )}
      
      {/* Fehler-Nachricht */}
      {file.status === "error" && file.error_message && (
        <p className="mt-2 text-xs text-red-500">
          {file.error_message}
        </p>
      )}
    </motion.div>
  );
};

export default function OCRPage() {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("deu");
  const [removeBlankPages, setRemoveBlankPages] = useState(false);
  const [languages, setLanguages] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const [analyseResult, setAnalyseResult] = useState(null);

  // Lade verfügbare Sprachen
  useEffect(() => {
    axios.get(`${API}/languages`)
      .then(res => setLanguages(res.data.languages))
      .catch(err => console.error("Fehler beim Laden der Sprachen:", err));
  }, []);

  // Polling für Status-Updates
  useEffect(() => {
    const hasProcessingFiles = files.some(f => f.status === "processing");
    
    if (!hasProcessingFiles) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/status`);
        const serverFiles = response.data.files;
        
        setFiles(prev => prev.map(file => {
          const serverFile = serverFiles.find(sf => sf.id === file.id);
          if (serverFile && serverFile.status !== file.status) {
            // Status hat sich geändert
            if (serverFile.status === "completed") {
              toast.success(`${file.name} wurde erfolgreich konvertiert`);
            } else if (serverFile.status === "error") {
              toast.error(`Fehler bei ${file.name}`);
            }
            return { ...file, ...serverFile };
          }
          return file;
        }));
      } catch (err) {
        console.error("Polling-Fehler:", err);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [files]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const validFiles = acceptedFiles.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return ALL_FORMATS.includes(ext);
    });
    
    if (validFiles.length === 0) {
      toast.error("Keine unterstützten Dateiformate");
      return;
    }

    const formData = new FormData();
    validFiles.forEach(file => formData.append('files', file));
    
    try {
      const response = await axios.post(`${API}/upload`, formData);
      
      const newFiles = response.data.files
        .filter(f => !f.error)
        .map(f => ({
          id: f.id,
          name: f.name,
          type: f.type,
          size: f.size,
          status: f.status
        }));
      
      setFiles(prev => [...prev, ...newFiles]);
      
      if (newFiles.length > 0) {
        toast.success(`${newFiles.length} Datei(en) hinzugefügt`);
      }
    } catch (error) {
      toast.error("Fehler beim Hochladen");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': SUPPORTED_FORMATS.images,
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc']
    }
  });

  const handleProcess = async () => {
    const pendingFiles = files.filter(f => f.status === "pending");
    if (pendingFiles.length === 0) {
      toast.info("Keine Dateien zum Verarbeiten");
      return;
    }

    setIsProcessing(true);
    
    // Setze alle pending auf processing
    setFiles(prev => prev.map(f => 
      f.status === "pending" ? { ...f, status: "processing" } : f
    ));

    try {
      await axios.post(`${API}/process`, {
        file_ids: pendingFiles.map(f => f.id),
        language: selectedLanguage,
        remove_blank_pages: removeBlankPages
      });
      
      toast.info("Verarbeitung gestartet...");
    } catch (error) {
      toast.error("Fehler beim Starten der Verarbeitung");
      setFiles(prev => prev.map(f => 
        f.status === "processing" ? { ...f, status: "pending" } : f
      ));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(`${API}/download/${file.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${file.name.split('.')[0]}_ocr.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const handleDownloadAll = async () => {
    const completedFiles = files.filter(f => f.status === "completed");
    for (const file of completedFiles) {
      await handleDownload(file);
    }
  };

  const handleSendToPosteingang = async (file) => {
    setSendingId(file.id);
    try {
      const response = await axios.post(`${API}/hephaistion/to-posteingang?file_id=${file.id}`);
      const data = response.data;
      if (data.success) {
        setFiles(prev => prev.map(f => f.id === file.id ? { ...f, sent_to_posteingang: true } : f));
        setAnalyseResult(data);
        const zuordnung = data.auto_zuordnung;
        if (zuordnung) {
          toast.success(`Analysiert & zugeordnet: ${zuordnung.vorname} ${zuordnung.nachname}`);
        } else {
          toast.success('An Posteingang gesendet & analysiert');
        }
      } else {
        toast.error('Fehler beim Senden');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden zum Posteingang');
    } finally {
      setSendingId(null);
    }
  };

  const handleRemove = async (fileId) => {
    try {
      await axios.delete(`${API}/file/${fileId}`);
      setFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (error) {
      toast.error("Fehler beim Entfernen");
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/files/clear`);
      setFiles([]);
      toast.success("Alle Dateien gelöscht");
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const pendingCount = files.filter(f => f.status === "pending").length;
  const processingCount = files.filter(f => f.status === "processing").length;
  const completedCount = files.filter(f => f.status === "completed").length;
  const progress = files.length > 0 ? (completedCount / files.length) * 100 : 0;

  return (
    <div className="space-y-8" data-testid="ocr-page">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1 mb-3">
          <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-xs font-medium text-amber-700">KI-Agent aktiv</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-800">Hephaistion</h1>
        <p className="text-slate-500 mt-1">Dokumentenverarbeitung – OCR, Texterkennung &amp; Leerseiten-Entfernung</p>
      </div>

      {/* Optionen */}
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-xl p-6 border border-slate-200 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Languages className="w-5 h-5 text-slate-500" />
              <span className="font-medium">OCR-Sprache</span>
            </div>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-48" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(languages).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileX className="w-5 h-5 text-slate-500" />
              <span className="font-medium">Leere Seiten entfernen</span>
            </div>
            <Switch 
              checked={removeBlankPages} 
              onCheckedChange={setRemoveBlankPages}
              data-testid="blank-pages-switch"
            />
          </div>
        </div>
      </div>

      {/* Dropzone */}
      <div className="max-w-2xl mx-auto">
        <div
          {...getRootProps()}
          className={`dropzone ${isDragActive ? "active" : ""}`}
          data-testid="dropzone"
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            <div className="dropzone-icon">
              <FilePlus className="w-8 h-8" />
            </div>
            <p className="text-lg font-medium">
              {isDragActive ? "Dateien hier ablegen..." : "Dateien hierher ziehen"}
            </p>
            <p className="text-sm text-slate-400 mt-1">
              oder klicken zum Auswählen
            </p>
            <p className="text-xs text-slate-400 mt-3">
              Unterstützt: PDF, DOCX, PNG, JPG, WEBP, TIFF
            </p>
          </div>
        </div>
      </div>

      {/* Dateiliste */}
      {files.length > 0 && (
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Aktionen */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span>{files.length} Dateien</span>
              {processingCount > 0 && (
                <span className="text-blue-600">
                  {processingCount} in Verarbeitung
                </span>
              )}
              {completedCount > 0 && (
                <span className="text-emerald-600">
                  {completedCount} fertig
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {completedCount > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadAll}
                  data-testid="download-all-btn"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Alle herunterladen
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                className="text-red-500 hover:bg-red-50"
                data-testid="clear-all-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Alle löschen
              </Button>
            </div>
          </div>

          {/* Fortschritt */}
          {files.length > 0 && (
            <Progress value={progress} className="h-2" />
          )}

          {/* Dateien */}
          <div className="space-y-3">
            <AnimatePresence>
              {files.map(file => (
                <FileCard
                  key={file.id}
                  file={file}
                  onRemove={handleRemove}
                  onDownload={handleDownload}
                  onSendToPosteingang={handleSendToPosteingang}
                  sendingId={sendingId}
                />
              ))}
            </AnimatePresence>
          </div>

          {/* Analyse-Ergebnis */}
          {analyseResult && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 space-y-3" data-testid="analyse-result">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-violet-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Claude-Analyse abgeschlossen
                </h3>
                <button onClick={() => setAnalyseResult(null)} className="text-violet-400 hover:text-violet-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {analyseResult.auto_zuordnung && (
                <div className="bg-white rounded-lg p-3 border border-violet-100">
                  <p className="text-xs text-slate-500">Automatisch zugeordnet</p>
                  <p className="text-sm font-medium text-slate-800">
                    {analyseResult.auto_zuordnung.vorname} {analyseResult.auto_zuordnung.nachname}
                  </p>
                </div>
              )}
              {analyseResult.analyse && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {analyseResult.analyse.dokumenttyp && (
                    <div className="bg-white rounded-lg p-2 border border-violet-100">
                      <span className="text-slate-500">Dokumenttyp:</span>
                      <span className="ml-1 font-medium text-slate-700">{analyseResult.analyse.dokumenttyp}</span>
                    </div>
                  )}
                  {analyseResult.analyse.absender && (
                    <div className="bg-white rounded-lg p-2 border border-violet-100">
                      <span className="text-slate-500">Absender:</span>
                      <span className="ml-1 font-medium text-slate-700">{analyseResult.analyse.absender}</span>
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-violet-600">
                OCR-Text: {analyseResult.ocr_text_length} Zeichen analysiert
              </p>
            </div>
          )}

          {/* Verarbeiten Button */}
          {pendingCount > 0 && (
            <Button
              className="w-full h-12 text-base"
              onClick={handleProcess}
              disabled={isProcessing || processingCount > 0}
              data-testid="process-btn"
            >
              {isProcessing || processingCount > 0 ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Verarbeitung läuft...
                </>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  {pendingCount} Datei(en) konvertieren
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
